import { ref } from 'vue'

import { ApiClientV2, apiClientV2, Collection } from '@/api/ApiClientV2'
import {
  FormFieldQueryBuilderMap,
  FormFieldType,
} from '@/components/common/forms/formBuilderHelper'
import {
  CellType,
  ListModelField,
  ModelField,
  TransientBaseObject,
} from '@/models/core/base'
import { Organisation } from '@/models/core/organisation'

import { getModelClass } from '../objectRegistry'
import {
  ALBUM_DEFAULT,
  ATTACHMENT_DEFAULT,
  contentTypes,
  GROUP_DEFAULT,
  GROUP_MEMBERSHIP_DEFAULT,
  OBJECT_AUTHORIZATION_DEFAULT,
  ROLE_DEFAULT,
} from './defaults'
import { Profile } from './profile'

export class Attachment extends TransientBaseObject {
  kind: string
  mime_type: string
  encoding: string
  file: string

  static apiUrl = 'attachment'
  static langPath: string = 'models.core.attachment'
  static objectType: string = 'attachment'

  static get defaultModel() {
    return JSON.parse(JSON.stringify(ATTACHMENT_DEFAULT))
  }
}

export class Role extends TransientBaseObject {
  name: string
  description: string
  organisation: string
  permissions: object

  static apiUrl = 'role'
  static langPath: string = 'models.core.role'
  static objectType: string = 'role'

  static filters = [
    {
      key: 'organisation',
      objectType: 'core.organisation',
    },
  ]

  static listFields: ListModelField[] = [
    { key: 'name' },
    { key: 'description' },
  ]

  static get defaultModel() {
    return JSON.parse(JSON.stringify(ROLE_DEFAULT))
  }

  static columns() {
    return this.defaultColumns(this.langPath, this.listFields)
  }

  static collection(
    filter: Collection.Filter,
    pagination: Collection.Pagination
  ) {
    return apiClientV2.subscribe<Role, Role>(this, filter, pagination)
  }
}

export class ObjectAuthorization extends TransientBaseObject {
  organisation: string
  object_type: string
  object_id: string
  role: string
  note: string
  propagation: ObjectAuthorization.Propagation
  group: string

  static apiUrl = 'object-authorization'
  static langPath: string = 'models.core.objectauthorization'
  static objectType: string = 'object-authorization'

  static listFields: ListModelField[] = [
    {
      key: 'role_name',
    },
    {
      key: 'group_name',
    },
    {
      key: 'object_type',
      sortable: true,
    },
    {
      key: 'object_name',
      tooltip: (row) => {
        return {
          label: row.tooltip,
          icon: 'mdi-information-variant',
        }
      },
    },
    {
      key: 'note',
      searchQuery: 'note_search',
      cellType: CellType.EDITABLE_CELL,
    },
  ]

  static get ancestors() {
    return [
      {
        modelClass: Organisation,
      },
      {
        modelClass: Role,
      },
      {
        modelClass: Group,
      },
    ]
  }
  static get joins(): Collection.RelatedAnnotation<ObjectAuthorization>[] {
    return [
      {
        relatedModelClass: Role,
        relatedObjectProperty: 'name',
      },
      {
        relatedModelClass: Group,
        relatedObjectProperty: 'name',
      },
    ]
  }
  static get annotations(): Collection.Annotation<
    ObjectAuthorization,
    string
  >[] {
    return [
      {
        key: 'object_name',
        callback: async (
          authorization: ObjectAuthorization,
          api: ApiClientV2
        ) => {
          let name = ''
          try {
            const apiName = contentTypes.find(
              (t) => t.name === authorization.object_type
            ).apiName
            const modelClass = getModelClass(apiName)
            const obj: any = await api.get(modelClass, authorization.object_id)
            if (!obj) {
              name = authorization.object_id
            } else if (obj && obj.name) {
              name = obj.name
            } else if (obj && obj.title) {
              name = obj.title
            } else {
              name = authorization.object_id
            }
            return {
              id: authorization.id,
              annotations: {
                object_name: name,
                tooltip: authorization.object_id,
              },
            }
          } catch (error) {
            console.warn('error', error)
            return {
              id: authorization.id,
              annotations: {
                object_name: authorization.object_id,
                tooltip: authorization.object_id,
              },
            }
          }
        },
      },
    ]
  }

  static columns() {
    return this.defaultColumns(this.langPath, this.listFields)
  }

  static collection(
    filter: Collection.Filter,
    pagination: Collection.Pagination
  ) {
    return apiClientV2.subscribe<ObjectAuthorization, ObjectAuthorization>(
      this,
      filter,
      pagination
    )
  }

  static get defaultModel() {
    return JSON.parse(JSON.stringify(OBJECT_AUTHORIZATION_DEFAULT))
  }
}

export namespace ObjectAuthorization {
  export enum Propagation {
    BOTH = 'both',
    PARENTS = 'parents',
    CHILDREN = 'children',
    NONE = 'none',
  }
}

export class Group extends TransientBaseObject {
  name: string
  description: string
  organisation: string

  static apiUrl = 'group'
  public static objectType: string = 'group'
  public static langPath: string = 'models.core.group'
  static listFields: ListModelField[] = [
    { key: 'name' },
    { key: 'description' },
  ]
  static fields: ModelField[] = [
    { key: 'name' },
    { key: 'description', required: false },
    {
      key: 'profiles',
      required: false,
      virtual: true,
      formFieldType: FormFieldType.PROFILES_SELECT,
    },
  ]
  static detailLinkQuery(context: { [key: string]: string }) {
    return FormFieldQueryBuilderMap[FormFieldType.PROFILES_SELECT](context)
  }

  static collection(
    filter: Collection.Filter,
    pagination: Collection.Pagination
  ) {
    return apiClientV2.subscribe<Group, Group>(this, filter, pagination)
  }

  static formConfig() {
    return {
      fields: this.defaultFormFields(this.langPath, this.fields),
      model: { ...GROUP_DEFAULT },
    }
  }

  static get defaultModel() {
    return JSON.parse(JSON.stringify(GROUP_DEFAULT))
  }
}

export class GroupMembership extends TransientBaseObject {
  group: string
  profile: string

  static apiUrl = 'group-membership'
  static objectType: string = 'group-membership'
  static langPath: string = 'models.core.groupmembership'
  static listFields: ListModelField[] = [
    { key: 'profile_username' },
    { key: 'profile_email' },
  ]
  static joins: Collection.RelatedAnnotation<GroupMembership>[] = [
    {
      relatedModelClass: Profile,
      relatedObjectProperty: 'username',
    },
    {
      relatedModelClass: Profile,
      relatedObjectProperty: 'email',
    },
  ]

  static collection(
    filter: Collection.Filter,
    pagination: Collection.Pagination
  ) {
    return apiClientV2.subscribe<GroupMembership, GroupMembership>(
      this,
      filter,
      pagination
    )
  }

  static get defaultModel() {
    return JSON.parse(JSON.stringify(GROUP_MEMBERSHIP_DEFAULT))
  }
}

export class Token extends TransientBaseObject {
  label: string
  key_hash: string
  profile: string
  enabled: boolean
}

export class PasswordResetRequest {
  username: string
  email: string
  password1: string
  password2: string
}

export class PasswordReset {
  id: string
  pin: string
  nonce: string
}

export class EmailUpdate {
  pin: string
  nonce: string
}

export class RequestEmailUpdate {
  email1: string
  email2: string
}

export class ProfileActivationRequest {
  username: string
  email: string
  password1: string
  password2: string
}

export class ProfileActivation {
  id: string
  nonce: string
  pin: string
}

export class BackgroundTask extends TransientBaseObject {
  state: string
  description: string
  result?: any
  progress?: {
    ratio: number
  }
}

export class Album extends TransientBaseObject {
  name: string
  description: string
  organisation: string
  public: boolean

  static langPath: string = 'models.core.album'
  static objectType: string = 'album'
  static fields: ModelField[] = [
    { key: 'name' },
    { key: 'description' },
    { key: 'public' },
  ]
  static listFields: ListModelField[] = [
    { key: 'name' },
    { key: 'description' },
    { key: 'public' },
  ]

  static formFields() {
    return this.defaultFormFields(this.langPath, this.fields)
  }

  static formConfig() {
    const config = {
      fields: Album.formFields(),
      model: { ...ALBUM_DEFAULT },
    }
    config.fields.forEach((field) => {
      if (field.key === 'public') {
        field.type = 'checkbox'
      }
    })
    return config
  }

  static columns() {
    return this.defaultColumns(this.langPath, this.listFields)
  }
}

export class Image extends TransientBaseObject {
  name: string
  album: string
  attachment: string
}
